import { useCallback, useRef, useState } from 'react';
import type { DocumentFileTypes } from '@zawarski/eti-ui';

type UploadResult = { success: boolean; totalFiles: DocumentFileTypes[] };
type UploadCallback = () => Promise<UploadResult>;

export type UploadOutcome =
  | { status: 'uploaded'; fileCount: number }
  | { status: 'failed'; reason: 'rejected' | 'timeout' | 'in-progress' };

/**
 * The Documents upload posts to an external Palmetto host and sets no timeout
 * of its own, so the caller owns the deadline. Expiry is treated as an upload
 * failure, which keeps the in-flight guard from holding the modal open forever.
 */
export const UPLOAD_TIMEOUT_MS = 30_000;

/** How long an upload runs before the modal explains why nothing has happened. */
export const UPLOAD_SLOW_NOTICE_MS = 8_000;

const TIMED_OUT = 'timed-out';

function createFileGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // randomUUID is missing in two cases, not one: outside a secure context (the
  // `--host 0.0.0.0` dev server reached over plain HTTP) and on browsers older
  // than roughly 2022, which managed government fleets still run. getRandomValues
  // carries neither restriction and has shipped since 2012, so the fallback stays
  // cryptographically random. That matters here because the API treats fileUuid
  // as a bearer capability -- a guessable group id would be a weak one.
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  // Last resort so the id generator can never throw during render -- there is no
  // ErrorBoundary above this modal, so throwing here would blank the app.
  const random = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${random()}-${random()}`;
}

/**
 * Owns the file group id, the queued-file state Documents writes into, and the
 * upload trigger the caller runs before sending its message.
 *
 * A fresh group id is minted on every transition of `open` from false to true,
 * so files from a previous message never attach to a new one. That holds whether
 * the caller unmounts the attachment UI on close or keeps it mounted -- the
 * requirement does not rest on the caller's mounting strategy.
 */
function useFileAttachment(open: boolean) {
  const [fileGroupId, setFileGroupId] = useState(createFileGroupId);
  // React's documented "adjust state when a prop changes" pattern: compare
  // against the previous render's value during render and set state right there.
  // Deliberately not an effect -- an effect would re-render twice per open and
  // would add to this repository's `react-hooks/set-state-in-effect` backlog.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    // Only on the closed -> open edge. Regenerating on close would strand the
    // files of a message whose send failed, which the retry path re-links by id.
    if (open) setFileGroupId(createFileGroupId());
  }
  // Documents owns both lists and only writes to them. This consumer reads the
  // upload result instead, but the props are typed as state setters and the
  // deleted callback is invoked with an updater function, so real setters go in.
  const [, setFilesQueued] = useState<DocumentFileTypes[]>([]);
  const [, setFilesDeleted] = useState<DocumentFileTypes[]>([]);
  const uploadCallbackRef = useRef<Nullable<UploadCallback>>(null);
  // Holds the real upload promise, which outlives the deadline that gave up on
  // it -- Promise.race abandons the loser, it does not cancel it.
  const runningUploadRef = useRef<Nullable<Promise<unknown>>>(null);

  const handleOnSave = useCallback((uploadCallback: UploadCallback) => {
    uploadCallbackRef.current = uploadCallback;
  }, []);

  // Shadow paths:
  //   happy:          upload resolves with at least one file in the group
  //   nil/null:       no upload callback registered (attachment area never
  //                   rendered, e.g. no file-store credential) -> nothing to
  //                   upload, zero files
  //   empty/zero:     nothing queued -> Documents resolves immediately with the
  //                   group's existing files, which is empty on a fresh group
  //   upstream error: upload rejects, reports failure, or outlives the deadline
  //                   -> failed, and the caller aborts the send
  //   re-entry:       a prior upload passed its deadline but is still running
  //                   -> failed, without starting a second one
  const triggerUpload = useCallback(async (): Promise<UploadOutcome> => {
    const upload = uploadCallbackRef.current;
    if (!upload) return { status: 'uploaded', fileCount: 0 };

    // A timed-out upload releases the caller's in-flight guard while still
    // working through the library's queue, which is cleared only on success.
    // Calling upload() again against that same queue posts every pending file a
    // second time, so the group ends up holding duplicates of each attachment.
    // Refuse instead, and let the caller tell the user to wait.
    if (runningUploadRef.current) return { status: 'failed', reason: 'in-progress' };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    // Settling the upload before racing it keeps a rejection that lands after
    // the deadline from surfacing as an unhandled rejection.
    const settled = upload().then(
      (result) => ({ ok: true as const, result }),
      (error: unknown) => ({ ok: false as const, error })
    );
    runningUploadRef.current = settled;
    // Cleared when the upload actually finishes, not when the race resolves --
    // that is the whole point of tracking it separately from the deadline.
    void settled.finally(() => {
      if (runningUploadRef.current === settled) runningUploadRef.current = null;
    });
    const expired = new Promise<typeof TIMED_OUT>((resolve) => {
      timeoutId = setTimeout(() => resolve(TIMED_OUT), UPLOAD_TIMEOUT_MS);
    });

    try {
      const outcome = await Promise.race([settled, expired]);
      if (outcome === TIMED_OUT) return { status: 'failed', reason: 'timeout' };
      if (!outcome.ok) {
        // This branch alone logs here, because the rejection's message is the one
        // piece of context the caller cannot see -- it only receives the reason
        // code. The timeout and reported-failure branches below carry nothing the
        // caller's own log does not already record, so they stay silent rather
        // than emitting a second line for the same failure.
        //
        // Only the message: the upload URL carries the file-store access token
        // as a query parameter, so nothing that could hold a URL gets logged.
        console.error('File attachment upload threw', {
          fileGroupId,
          reason: outcome.error instanceof Error ? outcome.error.message : 'unknown error',
        });
        return { status: 'failed', reason: 'rejected' };
      }
      // totalFiles is checked rather than trusted from the type: it crosses a
      // dependency boundary, and a shape change in a future eti-ui would
      // otherwise surface as a TypeError the caller reports as a send failure.
      return outcome.result.success && Array.isArray(outcome.result.totalFiles)
        ? { status: 'uploaded', fileCount: outcome.result.totalFiles.length }
        : { status: 'failed', reason: 'rejected' };
    } finally {
      clearTimeout(timeoutId);
    }
  }, [fileGroupId]);

  return {
    fileGroupId,
    setFilesQueued,
    setFilesDeleted,
    handleOnSave,
    triggerUpload,
  };
}

export default useFileAttachment;
